import * as THREE from 'three';
import type { DatumEngine, HostFrameState } from '@datum-sdk/engine';
import { isViewWaypoint } from '@datum-sdk/scene-format';
import type { CameraRoute, ScenePoint, Waypoint } from '@datum-sdk/scene-format';

/**
 * Preloader choreography for splat scenes, whose flights are SDK scene data.
 *
 * This module plays routes; it does not build them. The four flights live in
 * the scene as `settings.cameraRoutes` (`routes/<scene>Routes.ts` today, a
 * backend payload tomorrow) under the fixed ids below, so the same flight can
 * be authored in Studio, served over the wire and played here unchanged.
 * Nothing about WHERE the camera goes is computed in this file — even the poses
 * the host needs (where the exit parks, where the preloader rests) are read off
 * the routes' landings.
 *
 * What is left is the part that can never be scene data: **when** each route
 * runs. Only the host knows that the splat has landed, that the loading bar has
 * faded, or that the viewer just tapped to skip — so the turntable loops until
 * it is stopped, and the exit follows.
 *
 *   enter (spins up onto the ring) → turntable (looping, until the splat is
 *   ready) → exit → [reveal] → flyover (opens with its own hold) → done
 *   ── skip → glide
 *
 * Chaining happens on `route:end`, in a microtask so the engine's own hand-back
 * (controls-mode restore, pivot carry) has run before the next route starts.
 *
 * Two engine behaviours shape this and are easy to trip over again:
 *
 * - **Per-segment time quantisation (CR C14).** A segment ends on the first
 *   frame at or past its duration and the overshoot is discarded, so the cost
 *   is per WAYPOINT. Measured: the same flight at 67 waypoints ran +38…48 %
 *   long, at 5 waypoints +1.0…7.8 %, as a smooth run of 12 +2.7…5.5 %, and
 *   every single-waypoint route lands within 1 %. It does NOT judder — sampled once per rendered frame, the
 *   frame-to-frame speed jump is p50 ~3 % / p90 ~19 % on frames that did not
 *   overrun, unchanged between a 54 fps and a 30 fps run. The defect costs
 *   time, not smoothness. Keep authored routes coarse.
 * - **`setCameraState({isAnimated})` flies an ARC in orbit mode** — swung
 *   round a pivot that slides from the live one onto the new aim — so the
 *   skip is a one-waypoint route instead, which is also the only way to reach
 *   the cubic-bezier the `EasingType` preset list lacks.
 */

/**
 * Route ids this choreography drives. A scene that wants this preloader must
 * carry routes under these names; everything else about them is the author's.
 */
export const PRELOADER_ROUTE_IDS = {
  enter: 'preview-enter',
  turntable: 'preview-turntable',
  exit: 'preview-exit',
  flyover: 'preloader-flyover',
} as const;

/** Host-built, from the live pose at the moment of the gesture. */
export const PRELOADER_SKIP_ROUTE_ID = 'preloader-skip';

/** cubic-bezier ≈ the legacy `easeInOutCubic` the skip glide used. */
export const EASE_IN_OUT_CUBIC: [number, number, number, number] = [0.65, 0, 0.35, 1];
const SKIP_SMOOTH_SPEED = 4;
const SKIP_SMOOTH_MIN_S = 0.8;
const SKIP_SMOOTH_MAX_S = 2.5;

const isPoint = (v: Waypoint['lookAt']): v is [number, number, number] =>
  Array.isArray(v) && v.length === 3;

export interface CameraPose {
  position: ScenePoint;
  lookAt: ScenePoint;
}

/**
 * Where a route leaves the camera: its last waypoint's position and aim. Null
 * for a route that ends on an orbit stop — that pose depends on where it began.
 */
export const routeLanding = (route: CameraRoute | undefined): CameraPose | null => {
  const last = route?.waypoints[route.waypoints.length - 1];
  if (!last) return null;
  if (isViewWaypoint(last)) return { position: last.position, lookAt: last.lookAt };
  return isPoint(last.target) && isPoint(last.lookAt)
    ? { position: last.target, lookAt: last.lookAt }
    : null;
};

const preloaderRoute = (routes: readonly CameraRoute[], id: string): CameraRoute | undefined =>
  routes.find((route) => route.id === id);

/** Where the exit parks the camera — the reveal starts from here. */
export const preloaderParkPose = (routes: readonly CameraRoute[]): CameraPose | null =>
  routeLanding(preloaderRoute(routes, PRELOADER_ROUTE_IDS.exit));

/** Where the preloader leaves the camera: the flyover's landing, else the exit's. */
export const preloaderRestPose = (routes: readonly CameraRoute[]): CameraPose | null =>
  routeLanding(preloaderRoute(routes, PRELOADER_ROUTE_IDS.flyover)) ?? preloaderParkPose(routes);

export interface PreloaderCinematic {
  /** Begin the preview tour (call on `scene:preview-shown`). */
  start: () => void;
  /**
   * Wind the tour down to the exit's landing (call when the splat is ready).
   * Resolves when parked. Idempotent.
   */
  requestExit: () => Promise<void>;
  /**
   * Play the flyover (call together with `setSceneState('done')` so the
   * flight runs in parallel with the splat reveal). Resolves when the flight
   * (or its skip glide) lands. No flyover route → resolves at once.
   */
  startFlyover: () => Promise<void>;
  /** Skip the flyover (glide to its landing). True if a skip started. */
  skip: () => boolean;
  dispose: () => void;
}

export const createNativeRouteCinematic = (engine: DatumEngine): PreloaderCinematic => {
  // `?dev` probe: authored vs actual duration per route, plus one camera sample
  // per RENDERED frame. `subscribeFrameRendered` fires once a frame, after the
  // frame was drawn, with the pose it was drawn from — the property an
  // `Object3D.updateMatrixWorld` patch does NOT have (it fires several times a
  // frame and catches mid-frame poses; that mistake once produced an entirely
  // fictitious judder finding). See skill `perf-triage`.
  // Session-only, mirrors `window.__crowTour`; removed on dispose.
  const probe =
    import.meta.env.DEV || new URLSearchParams(window.location.search).has('dev')
      ? ((window as unknown as Record<string, unknown>).__routeCinematic = {
          marks: [] as { route: string; authoredMs: number; actualMs: number; waypoints: number }[],
          samples: [] as { t: number; x: number; y: number; z: number }[],
        })
      : null;
  const startedAt = new Map<string, number>();

  const sampleFrame = (frame: HostFrameState): void => {
    if (!probe) return;
    const [x, y, z] = frame.position;
    probe.samples.push({ t: performance.now(), x, y, z });
  };
  const stopSampling = probe ? engine.subscribeFrameRendered(sampleFrame) : (): void => {};

  let disposed = false;
  let started = false;
  let phase: 'idle' | 'tour' | 'exiting' | 'parked' | 'flyover' | 'skipping' | 'done' = 'idle';
  let resolveExit: (() => void) | null = null;
  let resolveFlyover: (() => void) | null = null;

  const routeById = (id: string): CameraRoute | undefined =>
    engine.getCameraRoutes().find((r) => r.id === id);

  const currentPosition = (): THREE.Vector3 => {
    const s = engine.getCameraState();
    return new THREE.Vector3(s.position[0], s.position[1], s.position[2]);
  };

  /**
   * False when the engine declined: an id the scene does not carry, or a route
   * with no waypoints, plays nothing and never ends — a phase waiting on its
   * `route:end` would hold the preloader forever.
   */
  const play = (id: string): boolean => {
    // Asking for an id the scene does not carry only draws the engine's warning.
    if (!routeById(id)) return false;
    if (probe) startedAt.set(id, performance.now());
    engine.startCameraRoute(id);
    return engine.getActiveCameraRouteId() === id;
  };

  const park = (): void => {
    const pose = preloaderParkPose(engine.getCameraRoutes());
    if (pose) engine.setCameraState(pose);
    phase = 'parked';
  };

  const onRouteEnd = (event: { routeId: string }): void => {
    // Done is final: a route the dev player replays later is not part of this
    // choreography, and would rewind the phase and skew the probe's marks.
    if (disposed || phase === 'done') return;
    if (probe) {
      const from = startedAt.get(event.routeId);
      const route = routeById(event.routeId);
      if (from !== undefined) {
        probe.marks.push({
          route: event.routeId,
          authoredMs: (route?.waypoints ?? []).reduce((sum, wp) => sum + (wp.duration ?? 0), 0),
          actualMs: Math.round(performance.now() - from),
          waypoints: route?.waypoints.length ?? 0,
        });
      }
    }
    // The engine runs its hand-back (controls-mode restore, pivot carry) right
    // after this emit, synchronously — chain in a microtask so the next route
    // starts on the settled state rather than racing it.
    queueMicrotask(() => {
      if (disposed) return;
      switch (event.routeId) {
        case PRELOADER_ROUTE_IDS.enter:
          if (phase === 'tour') play(PRELOADER_ROUTE_IDS.turntable);
          break;
        case PRELOADER_ROUTE_IDS.exit:
          phase = 'parked';
          resolveExit?.();
          resolveExit = null;
          break;
        case PRELOADER_ROUTE_IDS.flyover:
        case PRELOADER_SKIP_ROUTE_ID:
          phase = 'done';
          // Nothing left to sample — the probe would otherwise keep appending a
          // frame per frame for as long as the scene lives.
          stopSampling();
          resolveFlyover?.();
          resolveFlyover = null;
          break;
      }
    });
  };
  engine.on('route:end', onRouteEnd);

  return {
    start: () => {
      if (disposed || started) return;
      started = true;
      phase = 'tour';
      // The authored routes carry no `poiId`, so the per-waypoint auto-select
      // probe is pure overhead.
      engine.setRuntimeOptions({ cameraRoute: { disableAutoSelectPoiOnWaypoint: true } });
      play(PRELOADER_ROUTE_IDS.enter);
    },

    requestExit: () => {
      if (disposed || phase === 'parked' || phase === 'done') return Promise.resolve();
      if (phase === 'exiting' || phase === 'flyover' || phase === 'skipping') {
        return Promise.resolve();
      }
      if (phase === 'idle') {
        // Preview never showed (instant load) — park without a visible flight.
        park();
        return Promise.resolve();
      }
      // 'exiting' before the stop: the enter route's own `route:end` chains the
      // turntable from a microtask, and this must not let it start behind us.
      phase = 'exiting';
      engine.stopCameraRoute();
      if (!play(PRELOADER_ROUTE_IDS.exit)) {
        park();
        return Promise.resolve();
      }
      return new Promise<void>((res) => {
        resolveExit = res;
      });
    },

    startFlyover: () => {
      if (disposed || phase === 'done') return Promise.resolve();
      // A tour-only scene (2000 B.C.E) carries no flyover: the exit's park is the rest.
      phase = 'flyover';
      if (!play(PRELOADER_ROUTE_IDS.flyover)) {
        phase = 'done';
        stopSampling();
        return Promise.resolve();
      }
      return new Promise<void>((res) => {
        resolveFlyover = res;
      });
    },

    skip: () => {
      if (disposed || phase !== 'flyover') return false;
      const landing = routeLanding(routeById(PRELOADER_ROUTE_IDS.flyover));
      if (!landing) return false;
      phase = 'skipping';
      const travel = currentPosition().distanceTo(new THREE.Vector3().fromArray(landing.position));
      const seconds = THREE.MathUtils.clamp(
        travel / SKIP_SMOOTH_SPEED,
        SKIP_SMOOTH_MIN_S,
        SKIP_SMOOTH_MAX_S,
      );
      engine.stopCameraRoute();
      // Host-built and host-owned: the start pose is wherever the viewer
      // interrupted, so this one cannot be authored ahead of time.
      engine.putCameraRoute({
        id: PRELOADER_SKIP_ROUTE_ID,
        waypoints: [
          {
            target: landing.position,
            lookAt: landing.lookAt,
            duration: seconds * 1000,
            easing: EASE_IN_OUT_CUBIC,
          },
        ],
      });
      play(PRELOADER_SKIP_ROUTE_ID);
      return true;
    },

    dispose: () => {
      if (disposed) return;
      disposed = true;
      // A route still playing — the turntable loops — keeps the engine animating
      // until it is stopped; a failed load disposes here and never reaches done.
      if (phase !== 'idle' && phase !== 'parked' && phase !== 'done') engine.stopCameraRoute();
      phase = 'done';
      engine.off('route:end', onRouteEnd);
      stopSampling();
      if (probe) delete (window as unknown as Record<string, unknown>).__routeCinematic;
      // Unblock any awaiting choreography (its own disposed-guards no-op it).
      resolveExit?.();
      resolveExit = null;
      resolveFlyover?.();
      resolveFlyover = null;
    },
  };
};
